import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { getIsAssetModalContentControlsCondensed } from '~/components/AssetModalV2/Content/Footer/contexts/getIsAssetModalContentControlsCondensed';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type AssetModalContentControlsDensityContextValue = {
  isCondensed: boolean;
};

const defaultContextValue: AssetModalContentControlsDensityContextValue = {
  isCondensed: false,
};

export const AssetModalContentControlsDensityContext =
  createContext<AssetModalContentControlsDensityContextValue>(defaultContextValue);

export type AssetModalContentControlsDensityProviderProps = {
  centerContentWidth: number;
  children: ReactNode;
};

export const AssetModalContentControlsDensityProvider = memo(
  ({ centerContentWidth, children }: AssetModalContentControlsDensityProviderProps) => {
    const value = useMemo(
      () => ({
        isCondensed: getIsAssetModalContentControlsCondensed(centerContentWidth),
      }),
      [centerContentWidth],
    );

    return (
      <AssetModalContentControlsDensityContext.Provider value={value}>
        {children}
      </AssetModalContentControlsDensityContext.Provider>
    );
  },
);

AssetModalContentControlsDensityProvider.displayName = 'AssetModalContentControlsDensityProvider';

export const useAssetModalContentControlsDensity = () => {
  const ctx = useContext(AssetModalContentControlsDensityContext);

  if (ctx === defaultContextValue) {
    const error = new Error(
      'useAssetModalContentControlsDensity must be used within AssetModalContentControlsDensityProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Asset Modal content controls density context not found',
    });
  }

  return ctx;
};
